import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge } from "@/components/Common";
import { STATO_CORSO, PRIORITA, fmtDate, fmtEuro } from "@/lib/constants";
import {
  GraduationCap, Users, Building2, Presentation, AlertTriangle, Loader2,
  TrendingUp, Clock, ArrowUpRight, FileClock,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

function KpiCard({ icon: Icon, label, value, accent, testid, to }) {
  return (
    <Link
      to={to}
      className="group block rounded-xl border bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-[#FDA4AF] hover:shadow-md"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-3 flex items-end justify-between">
        <span className="font-display text-3xl font-bold text-slate-900">{value}</span>
        <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-[#E11D48]" />
      </div>
    </Link>
  );
}

const TIPO_CLS = {
  COB: "bg-amber-50 text-amber-700 border-amber-200",
  "Co.Co.Co.": "bg-violet-50 text-violet-700 border-violet-200",
  "Personale INDAR": "bg-blue-50 text-blue-700 border-blue-200",
};

function ContrattiCard({ items }) {
  return (
    <div className="mt-6 rounded-xl border bg-white p-5" data-testid="scadenze-contratti-card">
      <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
        <FileClock className="h-5 w-5 text-[#E11D48]" /> Contratti in scadenza (prossimi 30 giorni)
        <span className="ml-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-[#E11D48]" data-testid="scadenze-contratti-count">{items.length}</span>
      </h3>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-slate-400">Nessun contratto COB, Co.Co.Co. o del Personale INDAR in scadenza nei prossimi 30 giorni.</p>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2">
          {items.map((s) => (
            <Link key={`${s.id}-${s.campo}`} to={s.route} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5 transition-colors hover:border-[#FDA4AF] hover:bg-rose-50/40" data-testid="scadenza-contratto-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge label={s.tipo} cls={TIPO_CLS[s.tipo]} />
                  <span className="truncate text-sm font-medium text-slate-800">{s.nome}</span>
                </div>
                <div className="mt-0.5 text-xs text-slate-400">{s.voce}</div>
              </div>
              <div className="pl-2 text-right">
                <div className={`text-sm font-semibold ${s.giorni <= 7 ? "text-rose-600" : "text-slate-700"}`}>{fmtDate(s.data)}</div>
                <div className="text-xs text-slate-400">{s.giorni === 0 ? "oggi" : `tra ${s.giorni} gg`}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [contratti, setContratti] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/dashboard/scadenze-contratti").then((r) => setContratti(r.data.items)).catch(() => setContratti(null));
  }, []);

  if (!stats)
    return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const statoData = Object.entries(stats.by_stato).map(([k, v]) => ({
    name: STATO_CORSO[k]?.label || k, value: v, key: k,
  }));
  const barColors = ["#E11D48", "#2563EB", "#059669", "#D97706", "#7C3AED", "#0D9488", "#64748B"];

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Benvenuto, ${user?.name?.split(" ")[0] || ""}`} subtitle="Panoramica operativa INDAR Formazione & Sviluppo" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard icon={GraduationCap} label="Corsi Attivi" value={stats.n_corsi_attivi} accent="bg-rose-50 text-[#E11D48]" testid="kpi-corsi" to="/corsi" />
        <KpiCard icon={Users} label="Iscrizioni" value={stats.n_iscrizioni} accent="bg-blue-50 text-blue-600" testid="kpi-iscrizioni" to="/persone" />
        <KpiCard icon={Building2} label="Aziende" value={stats.n_aziende} accent="bg-emerald-50 text-emerald-600" testid="kpi-aziende" to="/aziende" />
        <KpiCard icon={Presentation} label="Docenti e tutor" value={stats.n_docenti} accent="bg-amber-50 text-amber-600" testid="kpi-docenti" to="/docenti" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Corsi per stato */}
        <div className="rounded-xl border bg-white p-5 lg:col-span-2" data-testid="chart-corsi-stato">
          <h3 className="font-display text-lg font-semibold text-slate-900">Corsi per Stato</h3>
          <div className="mt-4 h-64" style={{ minHeight: 256 }}>
            <ResponsiveContainer width="100%" height={256} minWidth={0}>
              <BarChart data={statoData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={50} interval={0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {statoData.map((e, i) => <Cell key={i} fill={barColors[i % barColors.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Economics */}
        {user?.can_economics && stats.economics && (
          <div className="rounded-xl border bg-white p-5" data-testid="economics-card">
            <h3 className="font-display text-lg font-semibold text-slate-900">Economico (tutti i corsi)</h3>
            <div className="mt-4 space-y-4">
              <div className="rounded-lg bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">Preventivo totale</div>
                <div className="mt-1 font-display text-2xl font-bold text-slate-900">{fmtEuro(stats.economics.preventivo)}</div>
              </div>
              <div className="rounded-lg bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">Consuntivo totale</div>
                <div className="mt-1 font-display text-2xl font-bold text-slate-900">{fmtEuro(stats.economics.consuntivo)}</div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                <span className="text-slate-600">Scostamento: </span>
                <span className="font-semibold">{fmtEuro(stats.economics.consuntivo - stats.economics.preventivo)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {contratti && <ContrattiCard items={contratti} />}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Scadenze */}
        <div className="rounded-xl border bg-white p-5" data-testid="scadenze-card">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
            <Clock className="h-5 w-5 text-[#E11D48]" /> Scadenze Imminenti
          </h3>
          <div className="mt-4 space-y-2">
            {stats.scadenze.length === 0 && <p className="text-sm text-slate-400">Nessuna scadenza nei prossimi 60 giorni.</p>}
            {stats.scadenze.map((s, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge label={s.tipo} cls="bg-slate-100 text-slate-600 border-slate-200" />
                    <span className="truncate text-sm font-medium text-slate-800">{s.titolo}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 pl-2">
                  {s.in_ritardo && <AlertTriangle className="h-4 w-4 text-rose-500" />}
                  <span className={`text-sm font-medium ${s.in_ritardo ? "text-rose-600" : "text-slate-600"}`}>{fmtDate(s.data)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent corsi */}
        <div className="rounded-xl border bg-white p-5" data-testid="recent-corsi-card">
          <h3 className="font-display text-lg font-semibold text-slate-900">Ultimi Corsi Aggiornati</h3>
          <div className="mt-4 space-y-2">
            {stats.recent_corsi.map((c) => {
              const st = STATO_CORSO[c.stato] || STATO_CORSO.bozza;
              return (
                <Link key={c.id} to={`/corsi/${c.id}`} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5 hover:border-[#FDA4AF] hover:bg-rose-50/40 transition-colors">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-800">{c.titolo}</div>
                    <div className="font-mono text-xs text-slate-400">{c.codice}</div>
                  </div>
                  <div className="flex items-center gap-2 pl-2">
                    <Badge label={st.label} cls={st.cls} />
                    <ArrowUpRight className="h-4 w-4 text-slate-300" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
